import '.a{display:flex;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.b8{gap:8px;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.a3{gap:4px;}.ssr-plugin'
import '.g5{padding:0px;}.ssr-plugin'
import '.p{position:absolute;}.ssr-plugin'
import '.g6{inset:8px;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.du{display:none;}.ssr-plugin'
import React, { useState } from 'react'
import { Text } from '../Text/index.js'
import { colors } from '../../tokens/colors.js'
import { radius } from '../../utils/radius.js'
import { useClient } from '@based/sdk/react'
import { Style, styled } from 'inlines'
import { prettyNumber } from '@based/pretty-number'
import { Button } from '../Button/index.js'
import { Icon } from '../Icon/index.js'
import { FilePreviewItem } from './FilePreviewItem.js'
import { DropZone } from './DropZone.js'
import { IconButton } from '../IconButton/index.js'
import { Menu } from '../Menu/index.js'
import { useOverlay } from '../../hooks/useOverlay.js'
import { Dialog } from '../Dialog/index.js'
import { Stack } from '../Stack/index.js'
import { TextInput } from '../TextInput/index.js'
import { SelectUploadedFileModal } from '../RichTextEditor/Toolbar/SelectUploadedFileModal.js'

type FileInputProps = {
  stream?: string
  multiple?: boolean
  accept?: string
  limit?: number //in bytes
  disabled?: boolean
  label?: string
  description?: string
  onChange?: (
    file: File | void,
    updateProgress: (p: number) => void,
    url: string,
  ) => void
  value?: string
  style?: Style
}

type Status = 'initial' | 'uploading' | 'success' | 'error'

type FilePreview = {
  name: string
  size: number
  preview: string
  progress?: number
  type?: 'file' | 'audio' | 'code' | 'video' | 'image' | 'color'
}

const isValidFileType = (file: File, acceptedTypes: string[]): boolean => {
  return acceptedTypes.some((type) => {
    if (type.endsWith('/*')) {
      const baseType = type.split('/')[0]
      return file.type.startsWith(`${baseType}/`)
    }
    return type === file.type
  })
}

export const FileInput = ({
  stream = 'file-upload',
  multiple = true,
  accept,
  limit,
  value,
  onChange,
  label,
  description,
  disabled,
  style,
}: FileInputProps) => {
  const client = useClient()
  const ref = React.useRef<HTMLInputElement>()

  const abortControllersRef = React.useRef<{ [key: string]: AbortController }>(
    {},
  )

  const [internalStatus, setInternalStatus] = React.useState<Status>('initial')
  const [internalProgress, setInternalProgress] = React.useState(0)

  const [draggingOver, setDraggingOver] = useState(false)
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [newSrc, setNewSrc] = React.useState<string[]>([])

  const fileUploadHandler = async (files: any[]) => {
    if (!multiple && files.length > 1) {
      setErrorMessage('Please upload only one file')
      return
    }

    if (limit) {
      let fileSizes = 0
      for (let i = 0; i < files.length; i++) {
        fileSizes += files[i].size
      }
      if (fileSizes > limit) {
        setErrorMessage(
          `Please upload files smaller than ${prettyNumber(limit, 'number-bytes')}`,
        )
        return
      }
    }

    if (accept) {
      const acceptedTypes = accept.split(',').map((type) => type.trim())
      const invalidFiles = files.filter(
        (file) => !isValidFileType(file, acceptedTypes),
      )

      if (invalidFiles.length > 0) {
        const invalidFileNames = invalidFiles.map((f) => f.name).join(', ')
        setErrorMessage(
          `Invalid file type(s): ${invalidFileNames}. Accepted types: ${acceptedTypes.join(', ')}`,
        )
        return
      }
    }

    setErrorMessage(null)

    for (let i = 0; i < files.length; i++) {
      setFilePreviews((prev) => ({
        ...prev,
        [files[i].name]: {
          name: files[i].name,
          size: files[i].size,
          type: files[i].type,
          preview: URL.createObjectURL(files[i]),
        },
      }))
    }

    const uploadResults = await Promise.allSettled(
      files.map(async (file) => {
        try {
          const controller = new AbortController()
          abortControllersRef.current[file.name] = controller

          const res = await client.stream(
            stream,
            {
              contents: file,
              fileName: file.name,
              mimeType: file.type,
              size: file.size,
            },
            (progress) => {
              setFilePreviews((prev) => ({
                ...prev,
                [file.name]: {
                  ...prev[file.name],
                  progress: Math.round(progress * 100),
                },
              }))
            },
            // { signal: controller.signal }, // Pass abort signal to stream ???
          )
          // console.log('RES??', res)
          const uploadedSrc =
            stream === 'file-upload' ? res?.src : res?.src || res
          if (uploadedSrc) {
            setNewSrc((prev) => [...prev, uploadedSrc])
          }

          // Cleanup controller after success
          delete abortControllersRef.current[file.name]

          // console.log(`✅ ${file.name} uploaded successfully`)
          return uploadedSrc
        } catch (error) {
          console.error(`❌ Failed to upload ${file.name}: ${error}`)
          return null
        }
      }),
    )

    // Return the first successful upload URL
    const firstSuccessfulUpload = uploadResults.find(
      (result) => result.status === 'fulfilled' && result.value !== null,
    )

    return firstSuccessfulUpload && firstSuccessfulUpload.status === 'fulfilled'
      ? firstSuccessfulUpload.value
      : null
  }

  const areThereFiles = Object.keys(filePreviews).length > 0 || Boolean(value)

  const UrlDialogContent = () => {
    const [enteredUrl, setEnteredUrl] = React.useState('')

    return (
      <styled.div>
        <TextInput
          label="Link to file"
          value={enteredUrl}
          onChange={(v) => setEnteredUrl(v)}
          placeholder="https://example.jpg"
        />
        <Stack justify="end" gap={16} style={{ marginTop: 24 }}>
          <Button variant="border" onClick={() => Dialog.Close()}>
            Cancel
          </Button>
          <Button
            disabled={!enteredUrl}
            onClick={() => {
              onChange(null, null, enteredUrl)
              Dialog.Close()
            }}
          >
            Insert
          </Button>
        </Stack>
      </styled.div>
    )
  }

  const EnterUrlDialog = useOverlay(Dialog, {
    title: 'Insert file from link',
    children: (
      <Dialog.Content>
        <UrlDialogContent />
      </Dialog.Content>
    ),
    // @ts-ignore
    style: { width: 440, '& p': { textAlign: 'left !important' } },
  })

  return (
    <div className="a i b8 h"
    >
      {label && (
        <Text variant="subtext-regular" color="neutral60">
          {label}
        </Text>
      )}
      <DropZone
        setDraggingOver={setDraggingOver}
        fileUploadHandler={fileUploadHandler}
        errorMessage={errorMessage}
        style={{
          minHeight: areThereFiles ? 'auto' : multiple ? 120 : 62,
          ...style,
        }}
        onChange={onChange}
      >
        {areThereFiles ? (
          <div
            style={{
              background: draggingOver
                ? colors.neutral10Adjusted
                : 'transparent',
            }}
          >
            {value ? (
              <FilePreviewItem
                file={[] as any}
                src={value}
                onRemove={() => {
                  setFilePreviews([])
                  onChange(null, null, null)
                }}
              />
            ) : (
              Object.entries(filePreviews).map((file, idx) => {
                const newSrcString = newSrc[idx]
                return multiple ? (
                  <FilePreviewItem
                    key={idx}
                    idx={idx}
                    file={file}
                    src={newSrcString}
                    //    onRemove={(idx) => {
                    // console.log('ids', idx)
                    // console.log(
                    //   'remove this one',
                    //   filePreviews[Object.keys(filePreviews)[idx]],
                    // )
                    // const tempFiles = { ...filePreviews }
                    // delete tempFiles[Object.keys(filePreviews)[idx]]
                    // setFilePreviews(tempFiles)
                    //   }}
                  />
                ) : (
                  <FilePreviewItem
                    key={idx}
                    idx={idx}
                    file={file}
                    src={newSrcString}
                    onRemove={() => {
                      setFilePreviews([])
                      onChange(null, null, null)
                    }}
                  />
                )
              })
            )}
          </div>
        ) : (
          <div className="a i a3 g5 p g6 d c"
            style={{
              color: colors.neutral60,
              borderRadius: radius[12],
              background: draggingOver
                ? colors.neutral10Adjusted
                : 'transparent',
            }}
          >
            {multiple && <Icon variant="page" color="neutral60" />}
            <Text variant="subtext-regular" color="neutral60">
              {multiple
                ? `Add or drag file(s) here`
                : `Add or drag a file here`}
            </Text>
          </div>
        )}
      </DropZone>

      {errorMessage && (
        <div className="a a3 d"
          style={{
            color: colors.red100,
          }}
        >
          <Icon variant="error-filled" />
          <Text variant="display-medium" color="red100">
            {errorMessage}
          </Text>
        </div>
      )}

      {!disabled && (
        <div className="a b8 d">
          <Menu>
            <Menu.Trigger>
              <Button
                leadIcon="add"
                variant="border"
                disabled={!multiple && areThereFiles}
              >
                {multiple && !areThereFiles ? 'Add file(s)' : 'Add file'}
              </Button>
            </Menu.Trigger>
            <Menu.Items>
              <Menu.Item
                leadIcon="ended-arrow-up"
                onClick={() => {
                  Menu.Close()
                  ref.current.click()
                }}
              >
                Upload new
              </Menu.Item>
              <Menu.Separator />
              <SelectUploadedFileModal
                setFile={(file) => {
                  if (file.src) {
                    onChange?.(file as File, null, file.src)
                  }
                }}
              >
                <Button
                  leadIcon="image"
                  variant="ghost"
                  width="full"
                  style={{
                    '& > *': { justifyContent: 'start', paddingLeft: 2 },
                    '& > div:nth-child(2)': {
                      gap: '8px !important',
                    },
                  }}
                >
                  Pick from uploaded
                </Button>
              </SelectUploadedFileModal>
              <Menu.Item leadIcon="link-on" onClick={EnterUrlDialog}>
                From link
              </Menu.Item>
            </Menu.Items>
          </Menu>
          {areThereFiles && (
            <Menu>
              <Menu.Trigger>
                <IconButton icon="more-horizontal" />
              </Menu.Trigger>
              <Menu.Items>
                {!multiple && (
                  <Menu.Item
                    onClick={() => {
                      setFilePreviews([])
                      onChange(null, null, null)
                    }}
                    color="red"
                    leadIcon="close"
                  >
                    Clear
                  </Menu.Item>
                )}
                {multiple && (
                  <Menu.Item
                    onClick={() => setFilePreviews([])}
                    color="red"
                    leadIcon="close"
                  >
                    Clear all
                  </Menu.Item>
                )}
              </Menu.Items>
            </Menu>
          )}
        </div>
      )}

      {description && (
        <Text variant="display-regular" color="neutral60">
          {description}
        </Text>
      )}

      <input className="du"
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={async (e) => {
          if (!e.target.files.length) return
          const files = [...e.target.files]
          const uploadedUrl = await fileUploadHandler(files)

          const file = e.target.files?.[0]
          try {
            setInternalStatus('uploading')

            onChange?.(
              file,
              (p) => {
                setInternalProgress(p)
                if (p === 100) {
                  setInternalStatus('success')
                }
                console.log(file, 'form on change/?', p, '😄', uploadedUrl)
              },
              uploadedUrl,
            )
          } catch (e) {
            setInternalStatus('error')

            setInternalProgress(0)
            // if (inputRef.current) {
            //   inputRef.current.value = ''
            // }
          }
        }}
      />
    </div>
  )
}
